// src/components/work_orders/PartsUsed.js
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Text,
  VStack,
  HStack,
  Divider,
  useColorModeValue,
  Tag,
  Flex,
  Button,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Tooltip
} from '@chakra-ui/react';
import { MdAdd, MdInventory, MdBuild, MdReceipt } from 'react-icons/md';

// --- UPDATE: quantities / prices arrive as strings (DECIMAL) or null — NaN-safe maths
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const qtyOf = (item) => num(item.quantity_used ?? item.quantity);
const priceOf = (item) => num(item.unit_price ?? item.unit_cost ?? item.price);
const money = (v) => num(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// --- END UPDATE

const PartsUsed = ({ workOrder, usedItems, onAddParts }) => {
  // Theme colors
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headerColor = useColorModeValue('gray.700', 'gray.200');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');
  const summaryBg = useColorModeValue('blue.50', 'rgba(66, 153, 225, 0.1)');
  const summaryBorder = useColorModeValue('blue.100', 'blue.700');

  const calculateTotalCost = () => {
    return money((usedItems || []).reduce((acc, item) => acc + qtyOf(item) * priceOf(item), 0)); // --- UPDATE
  };

  return (
    <Box>
      {/* Header Actions */}
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <HStack>
            <Icon as={MdBuild} color="blue.500" boxSize={5} />
            <Text fontSize="lg" fontWeight="bold" color={headerColor}>Inventory Log</Text>
            <Badge colorScheme="blue" variant="subtle" borderRadius="full">
                {usedItems ? usedItems.length : 0} Items
            </Badge>
        </HStack>
        <Button 
            size="sm" 
            colorScheme="blue" 
            leftIcon={<Icon as={MdAdd} />} 
            onClick={onAddParts}
            variant="solid"
        >
          Log Parts Used
        </Button>
      </Flex>

      {/* Parts Table */}
      <Box 
        borderWidth="1px" 
        borderRadius="lg" 
        borderColor={borderColor} 
        overflow="hidden"
        bg={cardBg}
      >
        {(usedItems && usedItems.length > 0) ? (
          <Table variant="simple" size="sm">
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th>Part Name</Th>
                <Th isNumeric>Qty</Th>
                <Th isNumeric>Unit Price</Th>
                <Th isNumeric>Total</Th>
              </Tr>
            </Thead>
            <Tbody>
              {usedItems.map((item, index) => (
                <Tr key={item.id ?? `${item.item_id}-${index}`} _hover={{ bg: hoverBg }}>{/* --- UPDATE: stable key */}
                  <Td fontWeight="medium">
                      <HStack>
                          <Icon as={MdInventory} color="gray.400" />
                          <Text>{item.item_name || item.name || item.part_name || `Item ${item.item_id ?? ''}`}</Text>{/* --- UPDATE */}
                          {/* Optional: Add tag if item came from AI suggestion */}
                          {item.source === 'ai_suggestion' && (
                              <Tooltip label="Added via AI Recommendation">
                                  <Tag size="sm" colorScheme="purple" variant="solid" fontSize="10px" h="16px" minH="unset">AI</Tag>
                              </Tooltip>
                          )}
                      </HStack>
                  </Td>
                  <Td isNumeric>
                      <Badge variant="outline" colorScheme="gray">
                        {qtyOf(item)}{/* --- UPDATE */}
                      </Badge>
                  </Td>
                  <Td isNumeric color={textColor}>${money(priceOf(item))}</Td>{/* --- UPDATE: NaN-safe */}
                  <Td isNumeric fontWeight="bold" color={headerColor}>
                    ${money(qtyOf(item) * priceOf(item))}{/* --- UPDATE */}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : (
          <Flex direction="column" align="center" justify="center" py={8} px={4}>
            <Icon as={MdInventory} boxSize={10} color="gray.300" mb={2} />
            <Text color={textColor} fontStyle="italic">No parts have been logged for this work order.</Text>
          </Flex>
        )}
      </Box>

      {/* Cost Summary */}
      {(usedItems && usedItems.length > 0) && (
          <Box mt={4} p={3} bg={summaryBg} borderRadius="md" borderWidth="1px" borderColor={summaryBorder}>
            <Flex justifyContent="flex-end" alignItems="center">
                <HStack spacing={4}>
                    <HStack color="gray.600">
                        <Icon as={MdReceipt} />
                        <Text fontSize="sm" fontWeight="medium">Total Parts Cost:</Text>
                    </HStack>
                    <Text fontSize="xl" fontWeight="bold" color="blue.600">
                        ${calculateTotalCost()}
                    </Text>
                </HStack>
            </Flex>
          </Box>
      )}
    </Box>
  );
};

PartsUsed.propTypes = {
    workOrder: PropTypes.object,
    usedItems: PropTypes.array,
    onAddParts: PropTypes.func
};

export default PartsUsed;